import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { Label } from './ui/label.jsx';
import { Input } from './ui/input.jsx';
import { Button } from './ui/button.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select.jsx';

const DeviceSection = ({ title, devices, setDevices, fields }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [count, setCount] = useState(devices.length || 1);

  useEffect(() => {
    setCount(devices.length || 1);
  }, [devices]);

  const createDevices = () => {
    const newDevices = [];
    for (let i = 0; i < count; i++) {
      if (devices[i]) {
        newDevices.push(devices[i]);
      } else {
        const newDevice = {};
        fields.forEach(field => {
          newDevice[field.name] = '';
        });
        newDevices.push(newDevice);
      }
    }
    setDevices(newDevices);
    if (!isExpanded) setIsExpanded(true);
  };

  const updateDevice = (index, field, value) => {
    const newDevices = [...devices];
    newDevices[index] = {
      ...newDevices[index],
      [field]: value
    };
    
    // Auto-fill room from name (first word)
    if (field === 'name' && fields.some(f => f.name === 'room')) {
      const firstWord = value.split(' ')[0];
      if (firstWord && !newDevices[index].room) {
        newDevices[index].room = firstWord;
      }
    }
    
    setDevices(newDevices);
  };

  const removeDevice = (index) => {
    const newDevices = devices.filter((_, i) => i !== index);
    setDevices(newDevices);
    setCount(newDevices.length);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg mb-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
        data-testid={`${title.toLowerCase()}-toggle`}
      >
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
          <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded">
            {devices.length}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-600" />
        )}
      </button>
      
      {isExpanded && (
        <div className="p-4 sm:p-6 pt-0 border-t">
          {/* Count Control */}
          <div className="flex items-end gap-2 mb-4">
            <div className="flex-1">
              <Label htmlFor={`${title}-count`}>Count</Label>
              <Input
                id={`${title}-count`}
                type="number"
                min="0"
                value={count}
                onChange={(e) => setCount(parseInt(e.target.value) || 0)}
                className="w-full"
                data-testid={`${title.toLowerCase()}-count`}
              />
            </div>
            <Button
              onClick={createDevices}
              size="default"
              data-testid={`create-${title.toLowerCase()}-btn`}
            >
              <Plus className="w-4 h-4 mr-1" />
              Create
            </Button>
          </div>

          {/* Device List */}
          <div className="space-y-4">
            {devices.map((device, index) => (
              <div
                key={index}
                className="border rounded-lg p-4 space-y-3 relative"
                data-testid={`${title.toLowerCase()}-device-${index}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-gray-700">
                    {title.slice(0, -1)} {index + 1}
                  </h3>
                  <Button
                    onClick={() => removeDevice(index)}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`remove-${title.toLowerCase()}-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                
                {fields.map(field => (
                  <div key={field.name}>
                    <Label htmlFor={`${title}-${index}-${field.name}`}>
                      {field.label}
                    </Label>
                    {field.type === 'select' ? (
                      <Select
                        value={device[field.name] || ''}
                        onValueChange={(value) => updateDevice(index, field.name, value)}
                      >
                        <SelectTrigger data-testid={`${title.toLowerCase()}-${index}-${field.name}`}>
                          <SelectValue placeholder={`Select ${field.label}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {field.options.map(option => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id={`${title}-${index}-${field.name}`}
                        value={device[field.name] || ''}
                        onChange={(e) => updateDevice(index, field.name, e.target.value)}
                        placeholder={field.label}
                        data-testid={`${title.toLowerCase()}-${index}-${field.name}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceSection;